import React, {useEffect, useState} from "react";
import {Form, InputNumber, Radio, Space, Switch, Tag} from "antd";
import {matchFormat} from "../../utils";
import {Showable} from "../../components/Switcher";

// common bracket sizes up to max, plus (optionally) the highest even number; anything else via custom
const teamCountOptions = (max, withHighestEven) => {
  let opts = [4, 8, 16, 32].filter(n => n <= max).map(n => ({value: n, label: n}));
  const highestEven = max - (max % 2);
  if (withHighestEven && highestEven >= 4 && !opts.some(o => o.value === highestEven))
    opts.push({value: highestEven, label: highestEven});
  return opts;
};

// value is a string (as take_to_playoff is stored)
const TeamCountInput = ({value, onChange, options, max}) => {
  const [custom, setCustom] = useState(false);
  const isCustom = custom || (!!value && !options.some(o => o.value === Number(value)));

  const onRadio = (e) => {
    if (e.target.value === "custom") {
      setCustom(true);
    } else {
      setCustom(false);
      onChange(`${e.target.value}`);
    }
  };

  return <div className="space-y-2">
    <Radio.Group optionType="button" buttonStyle="solid" value={isCustom ? "custom" : Number(value)} onChange={onRadio}>
      {options.map(o => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
      <Radio value="custom">Custom</Radio>
    </Radio.Group>
    {isCustom && <div>
      <InputNumber min={4} max={max} value={value ? Number(value) : null} autoFocus
                   onChange={v => onChange(v ? `${v}` : undefined)} />
    </div>}
  </div>;
};

// rounds a bracket of n teams plays, keyed by rounds_format index
const ROUNDS = [
  {idx: 3, label: "Round 1", has: n => n > 8},
  {idx: 2, label: "Quarter Finals", has: n => n > 4},
  {idx: 1, label: "Semi Finals", has: () => true},
  {idx: 0, label: "Finals", has: () => true},
];

// common "By Round" setups, stored as custom rounds_format ([F, SF, QF, R1]).
// filled for all rounds so the same preset fits any bracket size
const PRESETS = {
  bo3_final: {
    rounds: ["best2of3_to_11", "to_15", "to_15", "to_15"],
    label: <Space size={4}>{matchFormat("best2of3_to_11")} Final, {matchFormat("to_15")} rest</Space>,
  },
  bo3_final_semis: {
    rounds: ["best2of3_to_11", "best2of3_to_11", "to_15", "to_15"],
    label: <Space size={4}>{matchFormat("best2of3_to_11")} Final+Semis, {matchFormat("to_15")} rest</Space>,
    minTeams: 5, // with 4 teams it's the same as plain 2/3→11
  },
};

const presetsFor = n => Object.keys(PRESETS).filter(k => !PRESETS[k].minTeams || n >= PRESETS[k].minTeams);

const matchPreset = (roundsFormat, n) => {
  if (!roundsFormat) return null;
  const idxs = ROUNDS.filter(r => r.has(n)).map(r => r.idx);
  return presetsFor(n).find(k => idxs.every(i => roundsFormat[i] === PRESETS[k].rounds[i])) || null;
};

const roundsSummary = (rounds, n) =>
  <Space size={8} wrap>
    {ROUNDS.filter(r => r.has(n)).map(r => <Space size={2} key={r.idx}>{r.label} {matchFormat(rounds[r.idx])}</Space>)}
  </Space>;

// selection is derived from playoff_format + rounds_format; Form.Item injects onChange for playoff_format
const FormatPicker = ({onChange, selected, onPick, children}) =>
  <Radio.Group optionType="button" buttonStyle="solid" value={selected} onChange={e => onPick(e.target.value, onChange)}>
    {children}
  </Radio.Group>;

const SeedSplitBar =({total, playoff, consolation}) => {
  const out = Math.max(total - playoff - consolation, 0);

  const seg = (n, cls, text) => n > 0 &&
    <div className={`${cls} flex items-center justify-center whitespace-nowrap overflow-hidden px-1`} style={{flexGrow: n}}>{text}</div>;

  return <div className="mb-4">
    <div className="text-xs text-slate-500 mb-1">Seed split ({total} teams)</div>
    <div className="flex h-6 rounded overflow-hidden text-xs border border-slate-200">
      {seg(playoff, "bg-blue-500 text-white", `Playoff 1–${playoff}`)}
      {seg(consolation, "bg-purple-500 text-white", `Consolation ${playoff + 1}–${playoff + consolation}`)}
      {seg(out, "bg-slate-200 text-slate-600", `Out ${playoff + consolation + 1}–${total}`)}
    </div>
  </div>;
};

const PlayoffSettingsFields = ({form, availableTeams = [], showConsolationSwitch = true}) => {
  const playoff_style = Form.useWatch('playoff_style', form);
  const playoff_format = Form.useWatch('playoff_format', form);
  const take_to_playoff = Form.useWatch('take_to_playoff', form);
  const with_consolation = Form.useWatch('with_consolation', form);
  const take_to_consolation = Form.useWatch('take_to_consolation', form);
  const rounds_format = Form.useWatch('rounds_format', form);
  const [customRounds, setCustomRounds] = useState(false);

  const maxTeams = availableTeams.length || 14;
  const playoffN = Number(take_to_playoff) || 0;
  const consolationN = with_consolation ? (Number(take_to_consolation) || 0) : 0;
  // with consolation, leave at least 4 teams for it
  const maxPlayoff = with_consolation ? maxTeams - 4 : maxTeams;
  const maxConsolation = maxTeams - playoffN;
  const canConsolation = maxTeams >= 8;

  // consolation means both playoff and consolation are brackets
  useEffect(() => {
    if (with_consolation && playoff_style !== 'bracket') {
      form.setFieldValue('playoff_style', 'bracket');
    }
  }, [with_consolation, playoff_style]);

  // drop team counts that no longer fit
  useEffect(() => {
    if (playoffN > maxPlayoff) {
      form.setFieldValue('take_to_playoff', undefined);
    }
    if (take_to_consolation && Number(take_to_consolation) > maxConsolation) {
      form.setFieldValue('take_to_consolation', undefined);
    }
  }, [playoffN, maxPlayoff, maxConsolation]);

  const countRules = (max) => [
    { required: true },
    { validator: (_, v) => !v || (Number(v) >= 4 && Number(v) <= max) ? Promise.resolve() : Promise.reject(`Must be 4–${max}`) },
  ];

  const formatOptions = () => {
    return <>
      <Radio value="to_11">{matchFormat("to_11")}</Radio>
      <Radio value="to_15">{matchFormat("to_15")}</Radio>
      <Radio value="to_21">{matchFormat("to_21")}</Radio>
      <Radio value="best2of3_to_11">{matchFormat("best2of3_to_11")}</Radio>
    </>
  }

  const roundLabel = (r) => {
    if (!with_consolation) return r.label;

    return <span>
      {r.label}
      {r.has(playoffN) && <Tag color="blue" className="ml-2">Playoff</Tag>}
      {r.has(consolationN) && <Tag color="purple">Consolation</Tag>}
    </span>;
  }

  const bracketN = Math.max(playoffN, consolationN);
  const isBracket = playoff_style === 'bracket';
  const preset = isBracket && playoff_format === 'custom' && !customRounds ? matchPreset(rounds_format, bracketN) : null;

  const onPickFormat = (v, onChange) => {
    if (PRESETS[v]) {
      setCustomRounds(false);
      form.setFieldValue('rounds_format', PRESETS[v].rounds);
      onChange('custom');
    } else {
      setCustomRounds(v === 'custom');
      onChange(v);
    }
  };

  return (
    <>
      <Showable v={showConsolationSwitch && canConsolation}>
        <Form.Item name="with_consolation" label="With Consolation" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Showable>

      <Showable v={with_consolation && playoffN > 0}>
        <SeedSplitBar total={maxTeams} playoff={playoffN} consolation={consolationN} />
      </Showable>

      <div className={with_consolation ? "grid grid-cols-2 gap-4" : ""}>
        <Form.Item name="take_to_playoff" label="Teams in Playoff" rules={countRules(maxPlayoff)}>
          <TeamCountInput max={maxPlayoff} options={teamCountOptions(maxPlayoff, !with_consolation)} />
        </Form.Item>
        <Showable v={with_consolation}>
          <Form.Item name="take_to_consolation" label="Teams in Consolation" rules={countRules(maxConsolation)}>
            <TeamCountInput max={maxConsolation} options={teamCountOptions(maxConsolation, false)} />
          </Form.Item>
        </Showable>
      </div>

      {/* hidden (not unmounted) so playoff_style is still watched and submitted */}
      <Form.Item name="playoff_style" label="Playoff Rounds" rules={[{ required: true}]} hidden={with_consolation}>
        <Radio.Group optionType="button" buttonStyle="solid">
          <Radio value="bracket">Bracket</Radio>
          <Radio value="topbottom4">Top/Bottom 4</Radio>
          <Radio value="every_2">Finals Round Only</Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item name="playoff_format" label="Playoff Format" rules={[{ required: true}]}
                 extra={<>
                   {preset && roundsSummary(PRESETS[preset].rounds, bracketN)}
                   {with_consolation && <div>Applies to both playoff and consolation</div>}
                 </>}>
        <FormatPicker selected={preset || playoff_format} onPick={onPickFormat}>
          <div className="space-y-2">
            <div>{formatOptions()}</div>
            {isBracket && <div>{presetsFor(bracketN).map(k => <Radio key={k} value={k}>{PRESETS[k].label}</Radio>)}</div>}
            {isBracket && <div><Radio value="custom">Custom…</Radio></div>}
          </div>
        </FormatPicker>
      </Form.Item>
      {/* keeps rounds_format in submitted values when a preset hides the per-round fields */}
      <Form.Item name="rounds_format" hidden noStyle />
      <Showable v={isBracket && playoff_format === 'custom' && !preset}>
        {ROUNDS.filter(r => r.has(bracketN)).map(r =>
          <Form.Item key={r.idx} name={["rounds_format", r.idx]} label={roundLabel(r)} rules={[{ required: true}]}>
            <Radio.Group optionType="button" buttonStyle="solid">
              {formatOptions()}
            </Radio.Group>
          </Form.Item>
        )}
      </Showable>
    </>
  );
};

export default PlayoffSettingsFields;
